<template>
  <div>
    <div class="grid modal-content-cols sm:grid-cols-1 md:grid-cols-2 digital-voucher-form">
      <div>
        <div class="column">
          <label>offer for customer</label>
          <input
            type="text"
            class="item-name-input"
            :class="{ error: !digitalVoucherDraftState.item_name }"
            v-model="digitalVoucherDraftState.item_name"
            maxlength="50"
            data-cy="digital-voucher-name-input"
          />
          <span class="form-field-notice">max 50 characters</span>
        </div>

        <div class="column">
          <label>offers per customer</label>
          <select
            v-model="digitalVoucherDraftState.max_redemptions"
            data-cy="digital-voucher-max-redemptions-select"
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="-1">unlimited</option>
          </select>
        </div>

        <div class="column">
          <label>expiration date</label>
          <span class="form-field-notice">expires at 11:59 PM</span>
          <sb-date
            :flatpickr="flatPickrConfig"
            :include="['date']"
            placeholder="-"
            format="E, MMM d, y"
            mode="single"
            :value="expirationDate.value"
            :class="{ error: !digitalVoucherDraftState.expiration_date}"
            class="expiration-date"
            @update:value="changedExpiration"
            data-cy="digital-voucher-expiration-date-input" />
          <span v-if="expirationDate.error" class="form-field-notice error">{{ expirationDate.error }}</span>
        </div>

        <div class="column">
          <div :class="['reward-description-toggle', 'toggle-section-btn', {'toggle': !rewardDescriptionExpanded }]" @click="rewardDescriptionExpanded = !rewardDescriptionExpanded">
            <label v-if="rewardDescriptionExpanded">- offer description</label>
            <label v-if="!rewardDescriptionExpanded">+ offer description</label>
          </div>
          <textarea v-if="rewardDescriptionExpanded"
            rows=3
            class="description-input mb-0"
            v-model="digitalVoucherDraftState.description"
            maxlength="250"
            data-cy="digital-voucher-description-input"
          />
          <span v-if="rewardDescriptionExpanded" class="form-field-notice">max 250 characters</span>
        </div>

        <div class="column">
          <div :class="['reward-uploadphoto-toggle', 'toggle-section-btn', {'toggle': !rewardUploadPhotoExpanded }]" @click="rewardUploadPhotoExpanded = !rewardUploadPhotoExpanded">
            <label v-if="rewardUploadPhotoExpanded">- upload photo(s)</label>
            <label v-if="!rewardUploadPhotoExpanded">+ upload photo(s)</label>
          </div>
          <div v-if="rewardUploadPhotoExpanded" class="row w-full">
            <div class="flex flex-col sm:flex-col md:flex-row">
              <div class="flex flex-col">
                <label class="fs-12">display image</label>
                <div class="flex flex-col">
                  <span v-if="displayImage.error" class="form-field-notice error">{{ displayImage.error }}</span>
                  <sb-loading-icon v-if="displayImage.loading" />
                  <template v-else-if="displayImage.file">
                    <div class="block">
                      <img :src="displayImage.file.thumb" alt="display image" />
                    </div>
                    <span class="remove-btn" @click="changedDisplayImage()" data-cy="digital-voucher-display-image-remove-button">remove</span>
                  </template>
                  <template v-else>
                    <a
                      v-file-upload:png,gif,jpeg="changedDisplayImage"
                      class="mb-1 upload-btn"
                      data-cy="digital-voucher-display-image-button">
                      <fai :icon="['fas', 'camera']" />
                    </a>
                  </template>
                </div>
              </div>

              <div class="divider-vertical mr-5 ml-5" />

              <div class="flex flex-row flex-auto">
                <div class="flex flex-auto flex-col">
                  <label class="fs-12">voucher image(s)</label>
                  <span v-if="voucherImage.error" class="form-field-notice error">{{ voucherImage.error }}</span>
                  <div class="flex flex-row">
                    <template v-for="image in voucherImages">
                      <div class="flex flex-col">
                        <span v-if="image.error" class="form-field-notice error">{{ voucherImage.error }}</span>
                        <sb-loading-icon v-if="image.loading" />
                        <template v-else-if="image">
                          <div class="block">
                            <img :src="image.thumb" alt="voucher image" />
                          </div>
                          <span class="remove-btn" @click="changedVoucherImage(image)" data-cy="digital-voucher-voucher-image-remove-button">remove</span>
                        </template>
                      </div>
                    </template>
                    <div class="flex flex-col" v-if="voucherImage.loading">
                      <sb-loading-icon/>
                    </div>
                    <template v-if="voucherImages.length < 3 && !voucherImage.loading">
                      <div class="flex flex-col">
                        <a
                          v-file-upload:png,gif,jpeg="changedVoucherImage"
                          :class="['mb-1', 'upload-btn', {'ml-2': voucherImages.length >  0}]"
                          data-cy="digital-voucher-voucher-image-button">
                          <fai :icon="['fas', 'camera']" />
                        </a>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="column">
          <div :class="['reward-product-url-toggle', 'toggle-section-btn', {'toggle': !rewardProductUrlExpanded }]" @click="rewardProductUrlExpanded = !rewardProductUrlExpanded">
            <label v-if="rewardProductUrlExpanded">- product url</label>
            <label v-if="!rewardProductUrlExpanded">+ product url</label>
          </div>
        </div>

        <div v-if="rewardProductUrlExpanded" class="column">
          <label>product url title</label>

          <input type="text"
                 class="product-url-name-input"
                 v-model="digitalVoucherDraftState.product_url_attributes['name']"
                 maxlength="50"
                 data-cy="digital-voucher-product-url-name-input"
          />
        </div>

        <div v-if="rewardProductUrlExpanded" class="column">
          <label>url</label>
          <input type="text"
                 :class="['product-url-name-input',{error: isInvalidUrl(digitalVoucherDraftState.product_url_attributes['value'])}]"
                 v-model="digitalVoucherDraftState.product_url_attributes['value']"
                 data-cy="digital-voucher-product-url-value-input"
          />
        </div>

        <!-- BEGIN OFFER EFFECTS  -->
        <div class="column">
          <div :class="['reward-offer-effects-toggle', 'toggle-section-btn', {'toggle': !rewardOfferEffectsExpanded }]" @click="rewardOfferEffectsExpanded = !rewardOfferEffectsExpanded">
            <label v-if="rewardOfferEffectsExpanded">- add effects</label>
            <label v-if="!rewardOfferEffectsExpanded">+ add effects</label>
          </div>
        </div>

        <div v-if="rewardOfferEffectsExpanded" v-for="(effect, index) in digitalVoucherDraftState.offer_effects_attributes" :key="index">
          <div class="column">
            <input type="hidden"
                   class="offer-effects-id-input"
                   v-model="digitalVoucherDraftState.offer_effects_attributes[index]['id']"
                   data-cy="digital-voucher-offer-effects-id-input"
            />

            <label>effect name {{ index + 1 }}</label>
            <input type="text"
                   class="offer-effects-name-input"
                   v-model="digitalVoucherDraftState.offer_effects_attributes[index]['name']"
                   maxlength="50"
                   data-cy="digital-voucher-offer-effects-name-input"
            />
          </div>

          <div class="column">
            <label>effect percentage {{ index + 1 }}</label>
            <input type="number"
                   :class="['offer-effects-value-input', {error: isValidPercentage(digitalVoucherDraftState.offer_effects_attributes[index]['value'])}]"
                   v-model="digitalVoucherDraftState.offer_effects_attributes[index]['value']"
                   maxlength="50"
                   data-cy="digital-voucher-offer-effects-value-input"
            />
          </div>
        </div>

        <div v-if="rewardOfferEffectsExpanded" class="column add-effect-btn">
          <div class="reward-new-offer-effect-toggle" @click="initializeNewOfferEffect()">
            <span v-if="rewardOfferEffectsExpanded && digitalVoucherDraftState.offer_effects_attributes.length < 10">+ add new effect</span>
          </div>
        </div>
        <!--  END OFFER EFFECTS  -->

        <div class="column">
          <div :class="['reward-pos-toggle', 'toggle-section-btn', {'toggle': !rewardPosExpanded }]" @click="rewardPosExpanded = !rewardPosExpanded">
            <label v-if="rewardPosExpanded">- add POS discount ID</label>
            <label v-if="!rewardPosExpanded">+ add POS discount ID</label>
          </div>
          <template v-if="rewardPosExpanded">
            <label>POS discount ID</label>
            <input type="text"
                   v-model="digitalVoucherDraftState.meta"
                   name="meta"
                   maxlength="255"
            />
            <label>POS discount value</label>
            <div class="meta_json_selection w-full">
              <select
                v-model="digitalVoucherDraftState.reward_type"
                name="reward_type"
                class="reward_type"
                id="reward_type">
                <option value=""></option>
                <option value="%">%</option>
                <option value="$">$</option>
              </select>
              <input type="number"
                     step="any"
                     min=0.00
                     name="reward_value"
                     class="reward_value"
                     v-model="digitalVoucherDraftState.reward_value"
                     id="reward_value"
              />
            </div>
          </template>
        </div>
      </div>
      <div class="digital-voucher-modal-preview flex column">
        <label class="w-full text-center"> voucher preview </label>
        <div class="preview-container flex flex-col w-full pr-4 pl-4 pb-4">
          <label class="redeem-label w-full">redeem offer</label>
          <div class="pt-3 pb-3">
            <template v-if="previewImages.length > 0">
              <div class="flex flex-row w-full items-center justify-center">
                <template v-for="image in previewImages">
                  <div class="flex flex-col">
                    <div class="block">
                      <img :class="[{ 'selected-preview-image': selectedImage.id == image.id }]" @click="togglePreviewImage(image, $event)":src="image.thumb" alt="image"/>
                    </div>
                  </div>
                </template>
              </div>
            </template>
            <template v-else>
              <div class="text-center">
                <fai class="blue-star" :icon="['far', 'star']" />
              </div>
            </template>
          </div>
          <label class="preview-offer-name text-center"> {{!digitalVoucherDraftState.item_name || digitalVoucherDraftState.item_name === '' ? 'offer name' : digitalVoucherDraftState.item_name}} </label>
          <div class="expires text-center mb-2"> {{formatExpirationDate}} </div>
          <div class="text-center" v-if="digitalVoucherDraftState.description"> {{digitalVoucherDraftState.description}} </div>
          <div v-if="digitalVoucherDraftState.product_url_attributes['value'] && digitalVoucherDraftState.product_url_attributes['name']" class="text-center p-3">
            <a><u>{{productUrlText}}</u></a>
          </div>
          <div class="flex flex-col pt-2 pb-2">
            <template v-for="effect in digitalVoucherDraftState.offer_effects_attributes">
              <div v-if="effect.name && effect.value" class="w-full flex mb-3 line-height-1" >
                <div class="w-2/6 break-words">{{!effect.name || effect.name === '' ? 'effect name' : effect.name}}</div>
                <div class="w-4/6 effect-progress-bar bg-gray-200 text-center">
                  <div class="effect-bar text-xs font-medium text-blue-100 text-center p-0.5 leading-none" :style="{width: Math.min(!effect.value || effect.value === '' ? 70 : effect.value, 100) +'%'}"/>
                  <div class="effect-value"> {{!effect.value || effect.value === '' ? 70 : effect.value}}% </div>
                </div>
              </div>
            </template>
          </div>
          <div class="flex justify-center">
            <button class="button proceed-button"> proceed to offer </button>
          </div>
          <a class="return-wallet m-3"> return to wallet</a>
        </div>
      </div>
    </div>

    <div class="mb-4 mx-2.5 flex items-center">
      <checkbox
        :checked.sync="digitalVoucherDraftState.apply_to_non_opt_in"
        class="h-5 w-5 flex-none"
        id="applyToNonOptIn"
      />
      <label class="leading-4 pl-1.5" for="applyToNonOptIn">
        issue offer to members who would qualify for this message but are not opted in (member offer)
      </label>
    </div>
  </div>
</template>

<script>
import { attachmentAPI } from '../../../../services/api/attachmentAPI.js';
import { format, isBefore, add } from "date-fns";
import { mapGetters } from "vuex";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import moment from 'moment-timezone'
import { isUrlFormatValidUtil } from "../../../../utilities";

import { Checkbox } from "@springbig/sb-components";

export default {
  components: {Checkbox},

  props: {
    digitalVoucherDraftState: Object,
    campaign: Object,
  },

  data() {
    return {
      displayImage: {
        file: null,
        loading: false,
        error: null
      },
      voucherImages: [],
      voucherImage: {
        file: null,
        loading: false,
        error: null
      },
      previewImages: [],
      selectedImage: Object,
      expirationDate: {
        value: null,
        error: null
      },
      rewardDescriptionExpanded: false,
      rewardProductUrlExpanded: false,
      rewardOfferEffectsExpanded: false,
      rewardUploadPhotoExpanded: false,
      rewardPosExpanded: false
    }
  },

  name: "digital-voucher-form",

  created() {
    this.digitalVoucherDraftState.type = null;
    this.expirationDate.value = !!this.digitalVoucherDraftState.expiration_date ? utcToZonedTime(this.digitalVoucherDraftState.expiration_date, this.merchantTimeZone) : null;
    this.refreshAttachments();

    if (!this.digitalVoucherDraftState.offer_effects_attributes.length) {
      let item = {name: null, value: null};
      this.digitalVoucherDraftState.offer_effects_attributes.push(item);
    }
  },

  methods: {
    changedExpiration(selectedDate) {
      this.expirationDate.value = selectedDate;
      this.expirationDate.error = this.validateExpirationDate(selectedDate);

      const formattedDate = selectedDate.length === 0 ? null : zonedTimeToUtc(selectedDate, this.merchantTimeZone).toJSON();
      this.digitalVoucherDraftState.expiration_date = this.expirationDate.error ? null : formattedDate
      this.digitalVoucherDraftState.expiration_date_local = this.expirationDate.error ? null : formattedDate
    },

    initializeNewOfferEffect() {
      let item = {name: null, value: null};
      this.digitalVoucherDraftState.offer_effects_attributes.push(item);
    },
    togglePreviewImage(image, event) {
      this.selectedImage = image;
    },
    async changedDisplayImage(file) {
      this.displayImage.loading = true;
      this.displayImage.error = null;

      try {
        if(!file && this.displayImage.file) {
          await attachmentAPI.delete(this.displayImage.file.uuid);
          this.digitalVoucherDraftState.has_preload_image = false;
        } else {
          if(!["image/png","image/gif","image/jpeg"].includes(file?.type)){
            throw "issue uploading image: file content type only png, jpg, jpeg or gif accepted"
          }
          await attachmentAPI.create(this.digitalVoucherDraftState.id, 'preload', 'Reward', file);
          this.digitalVoucherDraftState.has_preload_image = true;
        }
      } catch(e) {
        // TODO: Handle error here
        this.displayImage.error = e
      }

      await this.refreshAttachments();
      this.displayImage.loading = false;
    },

    async changedVoucherImage(file) {
      this.voucherImage.error = null;

      try {
        if(file.uuid) {
          await attachmentAPI.delete(file.uuid);
        } else {
          if(!["image/png","image/gif","image/jpeg"].includes(file?.type)){
            throw "issue uploading image: file content type only png, jpg, jpeg or gif accepted"
          }
          this.voucherImage.loading = true;
          await attachmentAPI.create(this.digitalVoucherDraftState.id, 'coupon', 'Reward', file);
        }
      } catch(e) {
        // TODO: Handle error here
        this.voucherImage.error = e
      }

      await this.refreshAttachments();
      this.voucherImage.loading = false;
    },

    async refreshAttachments() {
      if(this.digitalVoucherDraftState.id !== null) {
        const attachments = await attachmentAPI.forReward(this.digitalVoucherDraftState.id, this.campaign.id, "Campaign")

        if(Array.isArray(attachments)) {
          const preloads = attachments.filter((attachment) => attachment.label === "preload");
          if(preloads.length > 0) {
            this.displayImage.file = preloads[preloads.length - 1];
            this.digitalVoucherDraftState.has_preload_image = true;
            this.digitalVoucherDraftState.couponImage = preloads[preloads.length - 1];
          } else {
            this.digitalVoucherDraftState.couponImage = null;
            this.displayImage.file = null;
            this.digitalVoucherDraftState.has_preload_image = false;
          }

          const coupons = attachments.filter((attachment) => attachment.label === "coupon");
          if(coupons.length > 0) {
            this.voucherImages = coupons;
            this.digitalVoucherDraftState.voucherImages = coupons
          } else {
            this.voucherImages = [];
            this.digitalVoucherDraftState.voucherImages = []
          }
          this.previewImages = attachments.filter((attachment) => attachment.label === "coupon" || attachment.label === "preload")
          if (!this.previewImages.map(image => image.id).includes(this.selectedImage?.id)) {
            this.selectedImage = this.previewImages[0]
          }
        }
      }
    },

    validateExpirationDate(selectedDate){
      if(
        this.campaign.send_at_local &&
        isBefore(
          add(selectedDate, {days: 1}),
          new Date(this.campaign.send_at_local)
        )
      ){
        return "can't expire before campaign send time"
      }
      return null
    },
    isInvalidUrl(url) {
      return !url || !isUrlFormatValidUtil(url);
    },
    isValidPercentage(percentage) {
      const regex = new RegExp(/^[0-9][0-9]?$|^100$/)
      return !percentage || !regex.test(percentage)
    }
  },

  computed: {
    flatPickrConfig() {
      return {
        minDate: new Date()
      }
    },
    formatExpirationDate() {
      if (this.digitalVoucherDraftState.expiration_date) {
        return 'expires ' + moment(utcToZonedTime(this.digitalVoucherDraftState.expiration_date, this.currentMerchant.merchant_timezone)).format("MMMM Do, YYYY [11:59 pm]")
      } else {
        return 'expiration date'
      }
    },
    productUrlText() {
      if (this.digitalVoucherDraftState.product_url_attributes['name']) {
         return this.digitalVoucherDraftState.product_url_attributes['name']
      } else {
        return 'click here for more info'
      }
    },
    ...mapGetters(["merchantTimeZone","currentMerchant"])
  }
}
</script>

<style>
  .reward-new-offer-effect-toggle {
    font-family: "AileronLight", sans-serif;
    font-size: 0.8rem;
    color: #128ff9;
    cursor: pointer;
  }
  .fs-12 {
    font-size: 12px;
  }
  .voucher-preview-label {
    color: #4A4A4A;
    font-size: 30px;
    text-align: center;
  }
  .preview-container {
    border: 1px solid #128FF9;
    border-radius: 8px;
  }
  .redeem-label {
    color: #4A4A4A;
    font-size: 14px;
    opacity: 0.5691266741071429;
    text-align: center;
  }
  .preview-offer-name {
    color: #128FF9;
    font-size: 30px;
    line-height: 1.15;
  }
  .expires {
    color: #4A4A4A;
    font-size: 14px;
    line-height: 17px;
    opacity: 0.5691266741071429;
    text-align: center;
  }
  .return-wallet {
    color: #128FF9;
    font-size: 20px;
    line-height: 24px;
    text-align: center;
  }
  .proceed-button {
    color: #FFFFFF;
    font-size: 20px;
    line-height: 24px;
    text-align: center;
    padding: 0.85rem;
  }
  .effect-progress-bar {
    border: 1px solid #128FF9;
    background-color: rgba(18, 143, 249, 0.1);
    border-radius: 6px;
    height: 18px;
  }
  .effect-bar {
    background-color: #128FF9;
    color: #4A4A4A;
    border-radius: 6px;
    height: 100%;
  }
  .effect-value {
    width: 0;
    position: relative;
    left: 42%;
    top: -100%
  }
  .blue-star {
    color: #128FF9;
    width: auto !important;
    height: 130px !important;
  }
  .selected-preview-image {
    height: 130px !important;
  }
  .meta_json_selection .reward_type {
    width: 15%;
    float: left;
  }
  .meta_json_selection .reward_value {
    width: 82%;
    float: right;
  }
  .toggle-section-btn label {
    cursor: pointer;
  }
  .toggle label {
    color: #128ff9;
  }
  .line-height-1 {
    line-height: 1;
  }
  .column.add-effect-btn{
    align-items: end !important;
  }
  .digital-voucher-form .sb-loading-icon {
    width: 75px !important;
  }
  .upload-btn {
    margin-top: 8px;
  }
</style>
